import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, Cloud, ChartLine, LifeBuoy, HeartPulse } from "lucide-react";
import { Overline } from "./Reveal";
import { EasyDmarcScanner } from "./EasyDmarcScanner";

// Homepage, top half after the hero (EP-001 v1.1: D19, Section 4).
// Copy follows the EP-001 Foundation Build Prompt v1.0 and the EasiLinked design system's
// core messages.

const OUTCOMES = [
    {
        icon: ShieldCheck,
        title: "Reduce Cyber Risk",
        body: "Help organizations reduce exposure and build stronger resilience.",
    },
    {
        icon: Users,
        title: "Improve Productivity",
        body: "Help people work efficiently, collaborate effectively, and reduce unnecessary complexity.",
    },
    {
        icon: Cloud,
        title: "Modernize Operations",
        body: "Help organizations move beyond outdated systems and processes toward scalable modern operations.",
    },
    {
        icon: ChartLine,
        title: "Enable Growth",
        body: "Build a technology foundation capable of supporting organizational growth.",
    },
    {
        icon: LifeBuoy,
        title: "Strengthen Business Continuity",
        body: "Help organizations prepare to remain operational through disruption and recover confidently.",
    },
];

// Business Outcomes: five cards, no vendor names. The first three span two of six columns,
// the last two span three, so the row reads as one deliberate block on desktop.
export const Outcomes = () => (
    <section
        id="outcomes"
        data-testid="outcomes-section"
        aria-labelledby="outcomes-heading"
        className="border-t border-brand-line bg-brand-surface py-20 sm:py-24"
    >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="max-w-3xl">
                <Overline>Business Outcomes</Overline>
                <h2
                    id="outcomes-heading"
                    className="mt-5 font-heading text-[32px] leading-[38px] text-brand-text sm:text-[44px] sm:leading-[50px]"
                >
                    Helping Organizations Achieve Meaningful Business Outcomes
                </h2>
                <p className="mt-5 text-lg leading-[30px] text-brand-muted">
                    Technology is most valuable when it helps organizations reduce risk, improve operations, and
                    confidently move forward. EasiLinked aligns technology with business goals so technology
                    investments support meaningful outcomes.
                </p>
            </div>

            <ul className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6 lg:gap-6">
                {OUTCOMES.map(({ icon: Icon, title, body }, i) => (
                    <li
                        key={title}
                        className={`rounded-card bg-brand-bg p-6 ${i < 3 ? "lg:col-span-2" : "lg:col-span-3"} ${
                            i === 4 ? "sm:col-span-2 lg:col-span-3" : ""
                        }`}
                    >
                        <Icon className="h-6 w-6 text-brand-accent" aria-hidden="true" />
                        <h3 className="mt-4 font-heading text-xl text-brand-text">{title}</h3>
                        <p className="mt-2 text-base leading-[26px] text-brand-muted">{body}</p>
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

// Healthcare Secure™ teaser: a distinct, understated section rather than a sixth outcome.
export const HealthcareTeaser = () => (
    <section
        data-testid="healthcare-teaser"
        aria-labelledby="healthcare-teaser-heading"
        className="py-20 sm:py-24"
    >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid grid-cols-1 gap-10 rounded-card border border-brand-line p-8 sm:p-12 lg:grid-cols-12 lg:items-center">
                <div className="lg:col-span-8">
                    <Overline>Healthcare Secure™</Overline>
                    <h2
                        id="healthcare-teaser-heading"
                        className="mt-5 font-heading text-[28px] leading-[36px] text-brand-text sm:text-[36px] sm:leading-[44px]"
                    >
                        Healthcare organizations face unique technology and compliance challenges.
                    </h2>
                    <p className="mt-5 max-w-2xl text-lg leading-[30px] text-brand-muted">
                        Technology decisions in healthcare affect patient trust, security, compliance readiness, and
                        operational resilience. Healthcare Secure™ is our dedicated practice for physician offices,
                        specialty clinics, dental, behavioral health and outpatient practices.
                    </p>
                    <Link
                        to="/healthcare-secure"
                        data-testid="healthcare-teaser-cta"
                        className="mt-8 inline-flex h-11 items-center gap-2 rounded-control border-[1.5px] border-brand-control-border px-6 text-sm font-semibold text-brand-text transition-colors duration-150 hover:bg-brand-surface"
                    >
                        Explore Healthcare Secure™
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                </div>
                <div className="lg:col-span-4">
                    <figure className="rounded-card bg-brand-surface p-6">
                        <HeartPulse className="h-7 w-7 text-brand-accent" aria-hidden="true" />
                        <blockquote className="mt-4 font-heading text-xl leading-8 text-brand-text">
                            Technology should support patient care, not compete with it.
                        </blockquote>
                    </figure>
                </div>
            </div>
        </div>
    </section>
);

// Free Email Security Assessment: the EasyDMARC scanner in its own section. It still loads
// only when the visitor clicks "Scan My Domain" (D12).
export const ScannerSection = () => (
    <section
        id="email-security-assessment"
        data-testid="scanner-section"
        aria-labelledby="scanner-heading"
        className="border-t border-brand-line py-20 sm:py-24"
    >
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-5 sm:px-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6">
                <Overline>Free Email Security Assessment</Overline>
                <h2
                    id="scanner-heading"
                    className="mt-5 font-heading text-[32px] leading-[38px] text-brand-text sm:text-[44px] sm:leading-[50px]"
                >
                    See how well your domain is protected.
                </h2>
                <p className="mt-5 max-w-xl text-lg leading-[30px] text-brand-muted">
                    A quick, free check of your domain’s email authentication: DMARC, SPF and DKIM. It shows where
                    someone could send email that appears to come from you, and what to address first.
                </p>
            </div>
            <div className="lg:col-span-6">
                <div className="mx-auto w-full max-w-md">
                    <EasyDmarcScanner />
                </div>
            </div>
        </div>
    </section>
);
